import { ifDefined } from 'lit/directives/if-defined.js'
import { classMap } from 'lit/directives/class-map.js'
import { html, literal } from 'lit/static-html.js'
import { property } from 'lit/decorators.js'
import { ref } from 'lit/directives/ref.js'
import { nothing } from 'lit'
import type { PropertyValues } from 'lit'

import { WithFocus, AsSubmitter, anchor, compose } from '#utils/mixins'
import { ariaElements, delegateEvents } from '#utils/directives'
import { LfuiElement } from '#utils/element'
import skeletonStyles from '#utils/skeleton.css'
import { skeletonSrLabel } from '#utils/skeleton'
import type LfdsListRowContent from '#components/navigation/list-row-content/lfds-list-row-content.ts'

import styles from './lfds-list-row.css.ts'

const LINK = literal`a`
const BUTTON = literal`button`
const DIV = literal`div`
const SPAN = literal`span`

/**
 * @name LfdsListRow
 *
 * @status ready
 * @tag lfds-list-row
 *
 * @slot start Leading content (icons, checkboxes, notifications)
 * @slot default Main content, typically using `<lfds-list-row-content>`
 * @slot end Trailing content (icons, checkboxes)
 *
 * @cssproperty --lfds-list-row-background_color - Background color of the row
 * @cssproperty --lfds-list-row-border_color - Border color of the row
 * @cssproperty --lfds-list-row-color - Primary text color (titleStart and titleEnd)
 * @cssproperty --lfds-list-row-color_secondary - Secondary text color (subtitleStart and subtitleEnd)
 *
 * The component renders as:
 * - `<div>` when `selects` is provided (selectable row with checkbox/radio)
 * - `<a>` (link) when `href` is provided
 * - `<button>` when `interactive` property is true or click handlers are attached
 * - `<span>` (non-interactive) otherwise for display-only content
 */
export default class LfdsListRow extends compose(
	LfuiElement,
	AsSubmitter,
	WithFocus
) {
	static styles = [LfuiElement.styles, skeletonStyles, styles]

	/**
	 * ID of the checkbox or radio element this row controls. When set, the row
	 * renders as a `<div>` that forwards clicks to the referenced control.
	 *
	 * This attribute only handles click forwarding and styling. To provide an
	 * accessible name for the control, use `sr-labelledby` on the checkbox/radio
	 * pointing to the relevant text elements in the row.
	 *
	 * @example
	 * ```html
	 * <lfds-list-row selects="cb-home">
	 *   <lfds-checkbox slot="start" id="cb-home" sr-labelledby="lbl-home"></lfds-checkbox>
	 *   <lfds-list-row-content>
	 *     <span id="lbl-home" slot="label-start">Hemförsäkring</span>
	 *   </lfds-list-row-content>
	 * </lfds-list-row>
	 * ```
	 */
	@property({ attribute: 'selects' }) selects: string

	/**
	 * Target browsing context when rendered as a link (`href` is set).
	 */
	@property() target: '_blank' | '_self'

	/**
	 * Relationship between the current document and the linked resource.
	 */
	@property() rel: string

	/**
	 * Language of the row content.
	 */
	@property() lang: string

	/**
	 * Link URL. When set, the row renders as an `<a>` element.
	 */
	@property() href: string

	/**
	 * Name of the underlying interactive element.
	 */
	@property() name: string

	/**
	 * Accessible name for screen readers (`aria-label`).
	 */
	@property({ attribute: 'sr-label' }) srLabel: string

	/**
	 * IDs of elements that label this row (`aria-labelledby`).
	 */
	@property({ attribute: 'sr-labelledby' }) srLabelledby: string

	/**
	 * IDs of elements that describe this row (`aria-describedby`).
	 */
	@property({ attribute: 'sr-describedby' }) srDescribedby: string

	/**
	 * IDs of elements controlled by this row (`aria-controls`).
	 */
	@property({ attribute: 'sr-controls' }) srControls: string

	/**
	 * Indicates whether the content controlled by this row is expanded
	 * (`aria-expanded`). Use with interactive or selectable rows that
	 * toggle visibility of related content (e.g. accordions).
	 */
	@property({ attribute: 'sr-expanded' }) srExpanded: 'true' | 'false'

	/**
	 * Indicates the type of popup triggered by this row (`aria-haspopup`).
	 * Use when the row opens a menu, dialog, or other popup.
	 */
	@property({ attribute: 'sr-haspopup' })
	srHaspopup: 'true' | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog'

	/**
	 * Forces interactive behavior when no `href` is provided.
	 *
	 * When `true`, the row renders as a `<button>`.
	 */
	@property({ type: Boolean }) interactive: boolean

	/**
	 * Shows a shimmering placeholder over the row's `start`/`end` slots
	 * instead of hiding them. If the row doesn't yet have a real destination
	 * or action (no `href`, `interactive`, or click handler set), it also
	 * renders as a non-interactive `<span>` — there's nothing to activate
	 * yet. If a destination/action is already known (e.g. `href` is set but
	 * some content, like a subtitle, is still loading), the row stays
	 * focusable and clickable, since activating it still does something
	 * real. Propagates to any slotted `<lfds-list-row-content>`.
	 */
	@property({ type: Boolean, reflect: true }) loading: boolean

	/**
	 * By default, `loading` only shows a shimmer for slots that have no real
	 * content assigned yet — an icon/content already present stays visible
	 * (e.g. a value simply being refreshed shouldn't disappear behind a
	 * shimmer). Set `force-shimmer` to also mask already-known content while
	 * `loading`. Propagates to any slotted `<lfds-list-row-content>`.
	 */
	@property({ type: Boolean, reflect: true, attribute: 'force-shimmer' })
	forceShimmer: boolean

	/**
	 * Overrides the generic loading screen-reader label. Pass a contextual
	 * string when this row's content is known (e.g. "Laddar bonsai-träd"), or
	 * `''` to suppress the label entirely when a wrapping region already
	 * announces loading for several grouped rows. Unset keeps the shared
	 * generic wording.
	 */
	@property({ attribute: 'sr-loading-label' }) srLoadingLabel?: string

	// =========================================================================
	// Lifecycle
	// =========================================================================

	connectedCallback() {
		super.connectedCallback()
		this.addEventListener('click', this.#handleLabelClick)

		// Initialize slot state attributes
		if (this.getAttribute('data-has-start') === null) {
			this.setAttribute('data-has-start', 'false')
		}
		if (this.getAttribute('data-has-end') === null) {
			this.setAttribute('data-has-end', 'false')
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		this.removeEventListener('click', this.#handleLabelClick)
	}

	protected firstUpdated(changed) {
		super.firstUpdated(changed)
		this.#syncSlot('start')
		this.#syncSlot('end')
	}

	updated(changed: PropertyValues<this>) {
		if (changed.has('loading') || changed.has('forceShimmer')) {
			this.setAttribute('aria-busy', String(!!this.loading))

			// Propagate to any slotted `<lfds-list-row-content>` so a single
			// `loading`/`force-shimmer` pair on the row covers the whole row's content.
			for (const content of this.querySelectorAll<LfdsListRowContent>(
				'lfds-list-row-content'
			)) {
				content.loading = this.loading
				content.forceShimmer = this.forceShimmer
			}
		}
	}

	// =========================================================================
	// Slot content detection
	// =========================================================================

	#handleSlotChange = (event: Event) => {
		const slot = event.target as HTMLSlotElement
		const name = slot.name as 'start' | 'end'
		if (name === 'start' || name === 'end') {
			this.#syncSlot(name, slot)
		}
	}

	#syncSlot(name: 'start' | 'end', slot?: HTMLSlotElement | null) {
		const target =
			slot ??
			this.renderRoot.querySelector<HTMLSlotElement>(`slot[name='${name}']`)
		if (!target) return

		const hasContent = target.assignedNodes({ flatten: true }).some((node) => {
			if (node.nodeType === Node.TEXT_NODE) {
				return (node.textContent ?? '').trim().length > 0
			}
			if (node.nodeType === Node.COMMENT_NODE) {
				return false
			}
			return true
		})

		const attr = `data-has-${name}`
		const value = hasContent ? 'true' : 'false'
		if (this.getAttribute(attr) !== value) {
			this.setAttribute(attr, value)
		}
	}

	// =========================================================================
	// Click handling
	// =========================================================================

	/**
	 * Forwards clicks to the referenced checkbox/radio when the row is selectable.
	 * Skips forwarding if the click originated from the control itself
	 * (to avoid double-toggling).
	 */
	#handleLabelClick = (event: Event) => {
		if (!this.selects) return

		const target = this.querySelector<HTMLElement>(`#${this.selects}`)
		if (!target) return

		// Don't forward if click originated from the control (already toggled)
		if (this.#eventOriginatedFromControl(event)) return

		target.click()
	}

	#eventOriginatedFromControl(event: Event): boolean {
		return event.composedPath().some((target) => {
			if (!(target instanceof Element)) return false

			if (target instanceof HTMLInputElement) {
				return target.type === 'checkbox' || target.type === 'radio'
			}

			return (
				target.localName === 'lfds-checkbox' ||
				target.localName === 'lfds-radio' ||
				target.getAttribute('role') === 'checkbox' ||
				target.getAttribute('role') === 'radio'
			)
		})
	}

	// =========================================================================
	// Tag resolution
	// =========================================================================

	private get _hasClickHandler(): boolean {
		return this.interactive || typeof this.onclick === 'function'
	}

	private get _hostTag() {
		if (this.selects) return DIV
		if (this.href != null) return LINK
		if (this._hasClickHandler) return BUTTON
		return SPAN
	}

	private get _isInteractiveTag(): boolean {
		const tag = this._hostTag
		return tag !== SPAN && tag !== DIV
	}

	private get _isSelectable(): boolean {
		return this._hostTag === DIV
	}

	// =========================================================================
	// Render
	// =========================================================================

	render() {
		const tag = this._hostTag
		const isInteractive = this._isInteractiveTag
		const isSelectable = this._isSelectable
		const loadingSrLabel = this.loading
			? skeletonSrLabel(this, this.srLoadingLabel)
			: ''

		return html`
      <${tag}
        class=${classMap({
					row: true,
					'focus-visible': this.focusVisible,
					interactive: isInteractive,
					selectable: isSelectable
				})}

        aria-label=${ifDefined(isInteractive ? this.srLabel : undefined)}
        aria-expanded=${ifDefined(isInteractive ? this.srExpanded : undefined)}
        aria-haspopup=${ifDefined(isInteractive ? this.srHaspopup : undefined)}
        href=${ifDefined(this.href)}
        rel=${ifDefined(this.rel)}
        lang=${ifDefined(this.lang)}
        name=${ifDefined(this.name)}
        target=${ifDefined(this.target)}
        ${isInteractive ? ref(this[anchor]) : nothing}
        ${
					isInteractive
						? delegateEvents(this.delegatedEvents, {
								after: { click: this.handleClick }
							})
						: nothing
				}
        ${ariaElements({
					labelledBy: isInteractive ? this.srLabelledby : undefined,
					controls: isInteractive ? this.srControls : undefined,
					describedBy: isInteractive ? this.srDescribedby : undefined
				})}>
            <slot name="start" @slotchange=${this.#handleSlotChange}></slot>

            <div class="body">
              <slot></slot>
            </div>

            <slot name="end" @slotchange=${this.#handleSlotChange}></slot>
            ${loadingSrLabel ? html`<span class="hidden" role="status">${loadingSrLabel}</span>` : nothing}
      </${tag}>
    `
	}
}
